import React from 'react';

import { Icon } from '../Icon/Icon';
import { ScoreGauge } from '../ScoreGauge/ScoreGauge';
import { Reveal } from '../Reveal/Reveal';
import { Skeleton } from '../Skeleton/Skeleton';
import { COLORS, heroGlowForScore } from '../../theme/tokens';
import type { InsightsSummary, Recommendation, InsightMetric } from '../../mock-data/types';
import {
  Root,
  Body,
  Left,
  LeftText,
  Title,
  RecoCard,
  RecoRow,
  RecoLabel,
  MetricGauges,
  MetricGauge,
  MetricLabel,
} from './InsightsPanel.styles';

// Main / sub gauge diameters (ScoreGauge `size`), reused to size the placeholders.
const MAIN_GAUGE = 124;
const METRIC_GAUGE = 76;

export type InsightsPanelProps = {
  summary: InsightsSummary;
  animate: boolean;
  /** Recommendations variant (acquisition). */
  recommendations?: Recommendation[];
  onApplyRecommendation?: (event: React.MouseEvent<HTMLElement>) => void;
  /** Metric-gauges variant (operations). */
  metrics?: InsightMetric[];
  /** While true, the score gauge(s) + headline show placeholders (the endpoint is loading). */
  loading?: boolean;
};

/**
 * Score gauge + headline, with one of two right-hand variants: an AI
 * recommendation list (acquisition) or a row of metric sub-gauges (operations).
 * Shared so both root pages render the same skeleton. While `loading`, the score
 * gauge(s) + headline show placeholders; the metric labels (identity) stay.
 */
export const InsightsPanel = ({ summary, animate, recommendations, onApplyRecommendation, metrics, loading = false }: InsightsPanelProps): React.ReactElement => (
  <Root variant="warm" $glow={heroGlowForScore(summary.score)}>
    <Body>
      <Left>
        <Reveal loading={loading} placeholder={<Skeleton circle width={MAIN_GAUGE} height={MAIN_GAUGE} />}>
          <ScoreGauge score={summary.score} status={summary.status} progress={summary.progress} animate={animate} />
        </Reveal>
        <LeftText>
          <Reveal loading={loading} placeholder={<Skeleton width={180} height={16} radius={6} />}>
            <Title text={summary.title} tag="span" />
          </Reveal>
        </LeftText>
      </Left>

      {recommendations && onApplyRecommendation ? (
        <RecoCard>
          {recommendations.map((reco) => (
            <RecoRow key={reco.id} data-id={reco.id} onClick={onApplyRecommendation}>
              <Icon name={reco.icon} size={20} color={COLORS.accent} />
              <RecoLabel text={reco.label} tag="span" />
              <Icon name={reco.ctaIcon} size={26} color={COLORS.iconBright} />
            </RecoRow>
          ))}
        </RecoCard>
      ) : null}

      {metrics ? (
        <MetricGauges>
          {metrics.map((metric) => (
            <MetricGauge key={metric.id}>
              <Reveal loading={loading} placeholder={<Skeleton circle width={METRIC_GAUGE} height={METRIC_GAUGE} />}>
                <ScoreGauge score={metric.score} status="" progress={metric.progress} animate={animate} size={METRIC_GAUGE} />
              </Reveal>
              <MetricLabel text={metric.title} tag="span" />
            </MetricGauge>
          ))}
        </MetricGauges>
      ) : null}
    </Body>
  </Root>
);

export default InsightsPanel;
